import React from "react";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";

export function AuthForm({
  t,
  authMode,
  setAuthMode,
  formData,
  setFormData,
  authError,
  authBusy,
  showPassword,
  setShowPassword,
  onSubmit,
  onGoogleSignIn,
}) {
  return (
    <div className="space-y-3 md:space-y-4">
      <div>
        <p className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-[#EAB308] mb-2">
          {t.liveGateTitle}
        </p>
        <p className="text-xs text-slate-400 leading-relaxed mb-3 md:mb-4">
          {t.liveGateBody}
        </p>
      </div>
      <form onSubmit={onSubmit} className="space-y-2 md:space-y-3">
        {authMode === "signup" && (
          <input
            type="text"
            value={formData.displayName}
            onChange={(e) =>
              setFormData({
                ...formData,
                displayName: e.target.value,
              })
            }
            placeholder={t.displayName}
            className="w-full rounded-lg md:rounded-xl bg-black/40 border border-white/10 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm text-slate-100 placeholder:text-slate-600"
          />
        )}
        <input
          type="email"
          required
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder={t.email}
          className="w-full rounded-lg md:rounded-xl bg-black/40 border border-white/10 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm text-slate-100 placeholder:text-slate-600"
        />
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            required
            value={formData.password}
            onChange={(e) =>
              setFormData({
                ...formData,
                password: e.target.value,
              })
            }
            placeholder={t.password}
            className="w-full rounded-lg md:rounded-xl bg-black/40 border border-white/10 px-3 md:px-4 py-2 md:py-2.5 pr-8 md:pr-10 text-xs md:text-sm text-slate-100 placeholder:text-slate-600"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-2.5 md:right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors">
            {showPassword ?
              <EyeOff size={16} />
            : <Eye size={16} />}
          </button>
        </div>
        {authError && <p className="text-xs text-red-400">{authError}</p>}
        <motion.button
          type="submit"
          disabled={authBusy}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          className="w-full py-2 md:py-2.5 rounded-lg md:rounded-xl bg-gradient-to-r from-[#007BFF] to-[#05DFAD] text-white text-[10px] md:text-xs font-black uppercase tracking-widest disabled:opacity-50">
          {authBusy ?
            "..."
          : authMode === "signup" ?
            t.signUp
          : t.signIn}
        </motion.button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/10"></div>
          </div>
          <div className="relative flex justify-center text-xs">
            <span className=" px-2 text-slate-50">Or</span>
          </div>
        </div>

        <motion.button
          type="button"
          onClick={onGoogleSignIn}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          className="w-full py-2 md:py-2.5 rounded-lg md:rounded-xl bg-white text-[#0B0F19] text-[10px] md:text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-gray-100 transition-colors">
          <svg className="w-3.5 h-3.5 md:w-4 md:h-4" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Continue with Google
        </motion.button>

        <button
          type="button"
          onClick={() =>
            setAuthMode(authMode === "signin" ? "signup" : "signin")
          }
          className="w-full text-[9px] md:text-[10px] text-[#00BCF0] font-bold hover:underline">
          {authMode === "signin" ? t.needAccount : t.haveAccount}
        </button>
      </form>
    </div>
  );
}
